import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ShieldAlert, ShieldCheck, Search, Flag } from "lucide-react";
import { BrandLockup } from "../components/BrandLogo";

const RED_FLAGS = [
  ["They ask you to pay", "Real employers never charge for applications, training, equipment, visas or \"registration\". Any fee up front is the clearest sign of a scam."],
  ["Pay that's too good to be true", "\"Earn $5,000 a week from home, no experience needed\" is bait. Compare the pay with similar roles on trusted job boards."],
  ["Personal details too early", "Don't share bank details, passport or ID copies, or your NRIC/SSN before a verified written offer and a real interview."],
  ["Chat-only recruiting", "Interviews held only over WhatsApp, Telegram or text, with no video call or office visit, are a common scam pattern."],
  ["Pressure and urgency", "\"Limited slots, reply in 1 hour\" is meant to stop you from thinking. Real hiring takes days or weeks."],
  ["Vague role, no company", "No clear duties, no company name, or a company you can't find online (website, LinkedIn page, registered address)."],
  ["Free email addresses", "Recruiters using @gmail.com or @outlook.com instead of a company domain, or a domain that's slightly misspelt."],
  ["Odd tasks", "Reshipping parcels, receiving money into your account, buying gift cards or \"liking\" products for pay are scams, and sometimes illegal."],
];

const CHECKS = [
  "Search the company name plus \"scam\" or \"reviews\".",
  "Find the job on the company's own careers page.",
  "Check the recruiter's LinkedIn profile: real photo, history, connections at the company.",
  "Ask for a video interview and a written offer on company letterhead.",
  "Never move money for an employer, and never pay to get hired.",
];

export default function ScamGuide() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-canvas text-ink" data-testid="scam-guide-page">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-surface/90 border-b border-line">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="scam-guide-home-link" className="flex items-center group" aria-label="GetInterviewed home">
            <BrandLockup />
          </Link>
          <button data-testid="scam-guide-back" onClick={() => (window.history.state?.idx > 0 ? navigate(-1) : navigate("/"))} className="flex items-center gap-2 px-4 py-2 rounded-xl border border-line text-sm text-ink hover:bg-subtle transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-14">
        <div className="af-fade-up">
          <div className="font-mono2 text-xs uppercase tracking-widest text-warning mb-3 flex items-center gap-2">
            <ShieldAlert className="w-4 h-4" /> Stay safe
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">How to spot a job scam</h1>
          <p className="text-body mt-4 max-w-2xl text-base">
            Job scams target people who are keen to work. They're getting harder to spot. Here are the signs to watch for and what to check before you apply.
          </p>
        </div>

        <section className="mt-12">
          <h2 className="font-display text-lg font-bold mb-5 flex items-center gap-2"><Flag className="w-5 h-5 text-warning" /> Red flags</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {RED_FLAGS.map(([title, body], i) => (
              <div key={title} data-testid={`scam-flag-${i}`} className="rounded-xl border border-line bg-surface p-5 af-fade-up" style={{ animationDelay: `${i * 50}ms` }}>
                <div className="font-semibold text-warning">{title}</div>
                <p className="text-sm text-body mt-1.5 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-12 rounded-2xl border border-brand/30 bg-brand-soft p-6 sm:p-8">
          <h2 className="font-display text-lg font-bold mb-4 flex items-center gap-2"><Search className="w-5 h-5 text-brand" /> Quick checks before you apply</h2>
          <ul className="space-y-2.5">
            {CHECKS.map((c) => (
              <li key={c} className="flex gap-2.5 text-sm sm:text-base text-body">
                <ShieldCheck className="w-4 h-4 text-brand mt-1 shrink-0" /> {c}
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-12">
          <h2 className="font-display text-lg font-bold mb-3">Think you've been targeted?</h2>
          <p className="text-sm sm:text-base text-body leading-relaxed">
            Stop replying and don't send money or documents. If you've shared bank details, call your bank straight away. Report it to the job site where you found the post. In Singapore, call the ScamShield helpline on 1799 or report at scamshield.gov.sg. Elsewhere, contact your local police or national fraud reporting service.
          </p>
          <p className="text-xs text-faint mt-6">
            GetInterviewed flags suspicious posts automatically, but no check is perfect. Always trust your judgement.
          </p>
        </section>
      </main>
    </div>
  );
}
